/**
 * WHAT: Hook that monitors user activity (mouse, keyboard, touch) and
 *       automatically logs the user out after a configurable period of
 *       inactivity, with a countdown warning dialog beforehand.
 * WHY:  DAMA handles sensitive database access data. Regulatory and internal
 *       security policies require idle sessions to be terminated to prevent
 *       unauthorized access from unattended workstations.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import SECURITY_CONFIG from '../config/security.config';

/**
 * WHAT: Tracks user inactivity via DOM activity events (mousemove, keydown, etc.)
 *       and triggers a warning countdown followed by forced logout.
 * WHY:  Enforces the session timeout policy defined in SECURITY_CONFIG so that
 *       idle browser tabs do not remain authenticated indefinitely.
 *
 * @param {function} onLogout - Function to call when logout is triggered
 * @param {boolean} isActive - Whether to track inactivity (should be true when user is logged in)
 * @returns {object} - { showWarning, secondsRemaining, resetTimer, dismissWarning }
 */
const useInactivityLogout = (onLogout, isActive = true) => {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const showWarningRef = useRef(false); // Ref to track warning state for event handlers

  const { INACTIVITY_TIMEOUT, WARNING_BEFORE_LOGOUT, ACTIVITY_EVENTS } = SECURITY_CONFIG;

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Execute logout
  const executeLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    if (onLogout) {
      onLogout();
    }
  }, [clearAllTimers, onLogout]);

  // Start countdown timer for warning modal
  const startCountdown = useCallback(() => {
    const warningSeconds = Math.floor(WARNING_BEFORE_LOGOUT / 1000);
    setSecondsRemaining(warningSeconds);
    setShowWarning(true);
    showWarningRef.current = true;

    countdownRef.current = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          executeLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [WARNING_BEFORE_LOGOUT, executeLogout]);

  // Reset the inactivity timer
  const resetTimer = useCallback(() => {
    if (!isActive) return;

    lastActivityRef.current = Date.now();
    clearAllTimers();
    setShowWarning(false);
    showWarningRef.current = false;

    // Set timer for warning (timeout - warning time)
    const warningDelay = INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT;
    warningTimeoutRef.current = setTimeout(() => {
      startCountdown();
    }, warningDelay);

    // Set timer for final logout (as backup)
    timeoutRef.current = setTimeout(() => {
      executeLogout();
    }, INACTIVITY_TIMEOUT);
  }, [isActive, INACTIVITY_TIMEOUT, WARNING_BEFORE_LOGOUT, clearAllTimers, startCountdown, executeLogout]);

  // Dismiss warning and reset timer (user clicked "Stay Logged In")
  const dismissWarning = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    // Only reset if warning is not showing (use ref to avoid stale closure)
    if (!showWarningRef.current) {
      resetTimer();
    }
  }, [resetTimer]);

  // Set up event listeners
  useEffect(() => {
    if (!isActive) {
      clearAllTimers();
      setShowWarning(false);
      showWarningRef.current = false;
      return;
    }

    // Initial timer start
    resetTimer();

    // Add event listeners for user activity
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup
    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  return {
    showWarning,
    secondsRemaining,
    resetTimer,
    dismissWarning
  };
};

export default useInactivityLogout;
